import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { selectState as selectStateAuth } from "authentication/main.slice";
import Loader from "components/Loader/Loader";
import NumberInput from "Dashboard/components/NumberInput";
import { IGetResourcesDetailsResponse } from "Dashboard/scenes/Resources/domain/IGetResourceDetailsResponse";
import { RESOURCES_MODULE_NAME } from "Dashboard/scenes/Resources/domain/ResourcesModule";
import { useAppDispatch } from "hooks/hooks";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { DiffDays, FormatDate } from "utils/dateUtils";
import { FormatNumberOrNull, FormatNumberWithUnitOrNull } from "utils/numberUtils";
import { averageConsumptionSuffix } from "../../../helpers/resourceUtil";
import {
  getResource,
  getResourcePicture,
  selectState,
  updateAverageConsumptionOverride,
  updateResourceDetails,
} from "../details.slice";
import EstimatedValueTooltip from "./EstimatedValueTooltip";
import UploadPhotoModal from "./UploadPhotoModal";
import InfoTooltip from "components/Popup/InfoTooltip";
import DatePicker from "react-datepicker";

interface IProps {
  resourceId: number;
}

const ResourceDetails = ({ resourceId }: IProps) => {
  const [isOpenPhotoModal, setIsOpenPhotoModal] = useState<boolean>(false);
  const [valuesActive, setValuesActive] = useState(false);
  const { loading, resourceDetails, resourcePicture } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);
  const dispatch = useAppDispatch();

  const buttonHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setValuesActive(true);
  };

  useEffect(() => {
    async function getDetails() {
      if (resourceId) {
        try {
          dispatch(getResource(resourceId));
          dispatch(getResourcePicture(resourceId));
        } catch (error) { }
      }
    }
    getDetails();
  }, [dispatch, resourceId]);

  useEffect(() => {
    if (resourceDetails && resourceDetails.resource.name !== null) {
      setNameValue(resourceDetails.resource.name);
    } else {
      setNameValue("");
    }
    if (resourceDetails && resourceDetails.resource.serialNumber !== null) {
      setSerialNumberValue(resourceDetails.resource.serialNumber);
    } else {
      setSerialNumberValue("");
    }
    if (resourceDetails) {
      setNextServiceUsageAmount(resourceDetails.resource.nextServiceUsageAmount);
    }
    if (resourceDetails) {
      setLastKnownUsageAmount(resourceDetails.resource.lastKnownUsageAmount);
    }
    if (resourceDetails && resourceDetails.resource.leaseRate !== null) {
      setLeaseRateValue(resourceDetails.resource.leaseRate);
    } else {
      setLeaseRateValue(undefined);
    }
    if (resourceDetails && resourceDetails.resource.note !== null) {
      setNoteValue(resourceDetails.resource.note);
    } else {
      setNoteValue("");
    }
    if (resourceDetails) {
      setRemainingQuantityValue(resourceDetails.resource.remainingQuantityAmount)
    }
  }, [resourceDetails]);

  const [nameValue, setNameValue] = useState("");
  const [serialNumberValue, setSerialNumberValue] = useState("");
  const [leaseRateValue, setLeaseRateValue] = useState<number | undefined>(undefined);
  const [lastKnownUsageAmount, setLastKnownUsageAmount] = useState<number | undefined>(undefined);
  const [nextServiceUsageAmount, setNextServiceUsageAmount] = useState<number | undefined>(undefined);
  const [noteValue, setNoteValue] = useState("");
  const [remainingQuantityValue, setRemainingQuantityValue] = useState<number>(resourceDetails?.resource.remainingQuantityAmount || 0);
  const averageConsumptionValue = resourceDetails?.resource?.averageConsumptionOverride ?? resourceDetails?.latestAverageConsumption?.averageConsumption;
  const isResourceActive = resourceDetails?.resource.status === 'ACTIVE';


  const refreshResourcePicture = useCallback(async () => {
    try {
      dispatch(getResourcePicture(resourceId));
    } catch (err) {
      console.log(err);
    }
  }, [dispatch, resourceId]);

  const handleNameBlur = async () => {
    try {
      if (resourceDetails?.resource.name !== nameValue) {
        await dispatch(updateResourceDetails({ resourceId: resourceId, name: nameValue }))
        toast.success("Naziv resursa je uspešno izmenjen.");
      }
    } catch (error) {
      console.error("Error updating name:", error);
    }
  }

  const handleNoteBlur = async () => {
    if (resourceDetails?.resource.note !== noteValue) {
      try {
        await dispatch(updateResourceDetails({ resourceId: resourceId, note: noteValue }));
        toast.success("Napomena je uspesno izmenjena.");
      } catch (error) {
        console.error("Error updating note:", error);
      }
    }
  };

  const handleSerialNumberBlur = async () => {
    if ((resourceDetails?.resource.serialNumber ?? "") !== serialNumberValue) {
      try {
        await dispatch(updateResourceDetails({ resourceId: resourceId, serialNumber: serialNumberValue }));
        toast.success("Serijski broj je uspesno izmenjen.");
      } catch (error) {
        console.error("Error updating serial number:", error);
      }
    }
  };

  const handleLastKnownUsageAmountBlur = async () => {
    if (resourceDetails?.resource.lastKnownUsageAmount !== lastKnownUsageAmount) {
      try {
        await dispatch(updateResourceDetails({ resourceId: resourceId, lastKnownUsageAmount: lastKnownUsageAmount }));
        toast.success("Kilometraža/sati je uspesno izmenjeno.");
      } catch (error) {
        console.error("Error updating last known usage amount:", error);
      }
    }
  };

  const handleNextServiceUsageAmountBlur = async () => {
    if (resourceDetails?.resource.nextServiceUsageAmount !== nextServiceUsageAmount) {
      try {
        await dispatch(updateResourceDetails({ resourceId: resourceId, nextServiceUsageAmount: nextServiceUsageAmount }));
        toast.success("Sledeći servis je uspesno izmenjen.");
      } catch (error) {
        console.error("Error updating next service usage amount:", error);
      }
    }
  };

  const handleLeaseRateBlur = async () => {
    if (resourceDetails?.resource.leaseRate !== leaseRateValue) {
      try {
        await dispatch(updateResourceDetails({ resourceId: resourceId, leaseRate: leaseRateValue }));
        toast.success("Tarifa dnevnog najma je uspesno izmenjena.");
      } catch (error) {
        console.error("Error updating lease rate:", error);
      }
    }
  };


  const handleStatusChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value;
    try {
      await dispatch(
        updateResourceDetails({
          resourceId: resourceId,
          status: newStatus,
        })
      );
      toast.success("Status uspešno je izmenjen.");
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleValueBlur = async () => {
    try {
      if (resourceDetails?.resource.remainingQuantityAmount !== remainingQuantityValue) {
        await dispatch(updateResourceDetails({ resourceId: resourceId, remainingQuantityAmount: remainingQuantityValue }))
        toast.success("Kolicina resursa je uspešno izmenjena.");
      }
    } catch (error) {
      console.error("Error updating totalQuantity:", error);
    }
  }

  const updateAverageConsumptionChange = (newValue: number | null | undefined) => {
    let averageConsumption = newValue !== null && newValue !== undefined
      ? Math.max(newValue, 0)
      : null;
    dispatch(updateAverageConsumptionOverride(averageConsumption));
    updateAverageConsumptionForResource(resourceId, averageConsumption);
  };

  const updateAverageConsumptionForResource = useMemo(
    () =>
      debounce(async (resourceId: number, averageConsumption: number | null) => {
        try {
          dispatch(updateResourceDetails({ resourceId: resourceId, averageConsumption: averageConsumption }));
          toast.success("Prosečna potrošnja je uspešno izmenjena.");
        } catch (err) {
          console.log(err);
        }
      }, 1000),
    [dispatch]
  );

  const hasDetailedDetails = (resourceDetails: IGetResourcesDetailsResponse): boolean => {
    if (resourceDetails === undefined || resourceDetails === null) {
      return false;
    }
    const category = resourceDetails.resource.category;
    return category.name === 'VEHICLE'
      || category.name === 'MECHANIZATION'
      || category.name === 'MANUAL_MACHINE'
      || category.name === 'POWER_TOOL';
  }

  const getLastKnownUsageAmountComponent = () => {
    if (hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive) {
      return (<NumberInput
        formatAs="quantity"
        value={(lastKnownUsageAmount || "").toString()}
        onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
          if (parsedValue !== lastKnownUsageAmount) {
            setLastKnownUsageAmount(parsedValue);
          }
        }}
        onBlur={handleLastKnownUsageAmountBlur}
      />);
    } else {
      return FormatNumberOrNull(lastKnownUsageAmount);
    }
  }

  const getNextServiceUsageAmountComponent = () => {
    if (hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive) {
      return (<NumberInput
        formatAs="custom"
        customFormatSuffix={resourceDetails?.resource.usageUnit?.name}
        value={(nextServiceUsageAmount || "").toString()}
        onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
          if (parsedValue !== nextServiceUsageAmount) {
            setNextServiceUsageAmount(parsedValue);
          }
        }}
        onBlur={handleNextServiceUsageAmountBlur}
      />);
    } else {
      return FormatNumberOrNull(nextServiceUsageAmount);
    }
  }

  return (
    <div className="col-1 main-resources-info loading-container">
      {(loading || !resourceDetails) && <Loader />}
      {resourceDetails && (
        <div className="resource-grid">
          <div className="resource-image" onClick={hasFullAccessTo(user, RESOURCES_MODULE_NAME) ? () => { setIsOpenPhotoModal(true); } : undefined}>
            {resourcePicture && <img src={resourcePicture} width={76} alt="test" />}
          </div>
          {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
            ? (
              <input className="resource-name"
                onBlur={handleNameBlur}
                onChange={(e) => { setNameValue(e.target.value) }}
                value={nameValue} />
            )
            : (
              <span className="resource-name">
                {resourceDetails.resource.name}
              </span>
            )}
          <span className="resource-owner">
            {resourceDetails.resourceOwner ? `vlasnik: ${resourceDetails.resourceOwner.name}` : ''}
          </span>
          <span className="resource-quantity-and-status">
            <div className="resource-quantity">
              <div className="resource-quantity-row">
                <div className="resource-quantity-label">{"> stanje"}</div>
                {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                  ? (
                    <input className="resource-quantity-amount" type="number"
                      onBlur={handleValueBlur}
                      onChange={(e) => { setRemainingQuantityValue(parseInt(e.target.value)) }}
                      value={remainingQuantityValue} />
                  )
                  : (
                    <div className="resource-quantity-amount">{remainingQuantityValue}</div>
                  )}
              </div>
              <div className="resource-quantity-row">
                <div className="resource-quantity-label">{"> izdato "}</div>
                <div className="resource-quantity-amount">{resourceDetails.resource.chargedQuantityAmount}</div>
              </div>
            </div>
            {hasFullAccessTo(user, RESOURCES_MODULE_NAME)
              ? (
                <select
                  className={`resource-status ${isResourceActive ? "red" : "black"}`}
                  value={resourceDetails.resource.status}
                  onChange={handleStatusChange}
                >
                  <option className="red" value={'ACTIVE'}>
                    Aktivan
                  </option>
                  <option className="black" value={'INACTIVE'}>
                    Neaktivan
                  </option>
                </select>

              )
              : (isResourceActive
                ? (
                  <span className="resource-status read-only red">Aktivan</span>
                )
                : (
                  <span className="resource-status read-only black">Neaktivan</span>
                )
              )}
          </span>
          <span className="resource-details-year-label greyCell">god. proizvodnje</span>
          <span className="resource-details-year greyBorder">
            {resourceDetails.resource.yearOfProduction}
          </span>
          <span className="resource-details-value-label greyCell">vrednost:</span>
          <span className="resource-details-value greyBorder">
            {FormatNumberWithUnitOrNull(resourceDetails.resource.currentValue, 'rsd')}
            {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
              ? (<>
                <button onClick={buttonHandler}>
                  <DescriptionOutlinedIcon fontSize="small" />
                </button>
                <EstimatedValueTooltip
                  resourceId={resourceId}
                  historicValues={resourceDetails.resource.historicValues}
                  active={valuesActive}
                  setActive={setValuesActive}
                />
              </>)
              : (<></>)
            }
          </span>

          {hasDetailedDetails(resourceDetails) ? (<>
            <span className="resource-details-sn-label greyCell">serijski broj</span>
            <span className="resource-details-sn greyBorder">
              {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                ? (
                  <input
                    type="text"
                    name="serialNumber"
                    min="0"
                    onChange={(e) => setSerialNumberValue(e.target.value)}
                    onBlur={handleSerialNumberBlur}
                    value={serialNumberValue}></input>
                )
                : (<>{serialNumberValue}</>)}
            </span>

            <span className="resource-details-usage-km-label greyCell">kilometraža</span>
            <span className="resource-details-usage-km greyBorder">
              {resourceDetails.resource.usageUnit?.name === "km" &&
                getLastKnownUsageAmountComponent()}
            </span>
            <span className="resource-details-usage-h-label greyCell">sati</span>
            <span className="resource-details-usage-h greyBorder">
              {resourceDetails.resource.usageUnit?.name === "h" &&
                getLastKnownUsageAmountComponent()}
            </span>
            <span className="resource-details-total-consumption-label greyCell">goriva potrošio</span>
            <span className="resource-details-total-consumption greyBorder">
              {FormatNumberWithUnitOrNull(resourceDetails.totalFuelSpent, "l")}
            </span>
            <span className="resource-details-avg-consumption-label greyCell">potrošnja </span>
            <span className="resource-details-avg-consumption greyBorder width-fix">
              {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                ? (
                  <>
                    <NumberInput
                      formatAs="quantity"
                      value={(averageConsumptionValue || "").toString()}
                      onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                        if (parsedValue !== resourceDetails?.resource?.averageConsumptionOverride
                          && parsedValue !== resourceDetails?.latestAverageConsumption?.averageConsumption
                        ) {
                          updateAverageConsumptionChange(parsedValue);
                        }
                      }}
                    />
                    <span>{averageConsumptionSuffix(resourceDetails.resource.usageUnit)}</span>
                  </>
                )
                : (<span>{FormatNumberWithUnitOrNull(averageConsumptionValue, averageConsumptionSuffix(resourceDetails.resource.usageUnit))}</span>)}
            </span>

            <span className="resource-details-lease-label greyCell">najam</span>
            <span className="resource-details-lease greyBorder">
              {hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive
                ? (
                  <NumberInput
                    formatAs="rsd"
                    value={leaseRateValue !== undefined && leaseRateValue !== null ? leaseRateValue.toString() : ""}
                    onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                      setLeaseRateValue(parsedValue);
                    }}
                    onBlur={handleLeaseRateBlur}
                  />
                )
                : FormatNumberWithUnitOrNull(leaseRateValue, 'rsd', { maximumFractionDigits: 0 })
              }
            </span>

            <span className="resource-details-next-reg-at-label greyCell">sledeća registracija</span>
            <span className="resource-details-next-reg-at greyBorder">
              <DatePicker
                className="rounded-datePicker"
                dateFormat="dd.MM.yy"
                showMonthDropdown
                showYearDropdown
                onChange={(date: any) => {
                  dispatch(updateResourceDetails({ resourceId: resourceId, nextRegistrationDate: date }));
                  toast.success("Sledeća registracija je uspešno izmenjena.");
                }}
                selected={resourceDetails.resource.nextRegistrationDate ? new Date(resourceDetails.resource.nextRegistrationDate) : null}
                disabled={!(hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive)}
              />
            </span>
            <span className="resource-details-next-reg-in-label greyCell">za</span>
            <span
              className={`resource-details-next-reg-in
              ${resourceDetails.hasAlarmForRegistration
                  ? "greyBorder red"
                  : "greyBorder"}
            `}
            >
              {resourceDetails.resource.nextRegistrationDate &&
                `${DiffDays(resourceDetails.resource.nextRegistrationDate)} dana`}
            </span>

            <span className="resource-details-next-service-at-label greyCell">sledeći servis</span>
            {resourceDetails.resource.nextServiceDate
              ? (<InfoTooltip tooltip={FormatDate(resourceDetails.resource.nextServiceDate)} classesProp="resource-details-next-service-at greyBorder" element="span">
                {getNextServiceUsageAmountComponent()}
              </InfoTooltip>)
              : (<span className="resource-details-next-service-at greyBorder">
                {getNextServiceUsageAmountComponent()}
              </span>)
            }
            <span className="resource-details-next-service-in-label greyCell">za</span>
            <span
              className={`resource-details-next-service-in
              ${resourceDetails.hasAlarmForService
                  ? "greyBorder red"
                  : "greyBorder"}
            `}
            >
              {resourceDetails.resource.nextServiceUsageAmount &&
                FormatNumberWithUnitOrNull(
                  resourceDetails.resource.nextServiceUsageAmount -
                  resourceDetails.resource.lastKnownUsageAmount,
                  resourceDetails.resource.usageUnit &&
                  (resourceDetails.resource.usageUnit.name === "h"
                    ? " sati"
                    : " km")
                )}
              {resourceDetails.resource.nextServiceUsageAmount && resourceDetails.resource.nextServiceDate
                ? " / " : ""}
              {resourceDetails.resource.nextServiceDate &&
                `${DiffDays(resourceDetails.resource.nextServiceDate)} dana`}
            </span>

            <span className="resource-details-next-tacho-at-label greyCell">sledeći tahograf</span>
            <span className="resource-details-next-tacho-at greyBorder">
              <DatePicker
                className="rounded-datePicker"
                dateFormat="dd.MM.yy"
                showMonthDropdown
                showYearDropdown
                onChange={(date: any) => {
                  dispatch(updateResourceDetails({ resourceId: resourceId, nextTachographDate: date }));
                  toast.success("Sledeći tahograf je uspešno izmenjen.");
                }}
                selected={resourceDetails.resource.nextTachographDate ? new Date(resourceDetails.resource.nextTachographDate) : null}
                disabled={!(hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive)}
              />
            </span>
            <span className="resource-details-next-tacho-in-label greyCell">za</span>
            <span
              className={`resource-details-next-tacho-in
              ${resourceDetails.hasAlarmForTachograph
                  ? "greyBorder red"
                  : "greyBorder"}
            `}
            >
              {resourceDetails.resource.nextTachographDate &&
                `${DiffDays(resourceDetails.resource.nextTachographDate)} dana`}
            </span>

            <span className="resource-details-next-fire-at-label greyCell">sledeći pp aparat</span>
            <span className="resource-details-next-fire-at greyBorder">
              <DatePicker
                className="rounded-datePicker"
                dateFormat="dd.MM.yy"
                showMonthDropdown
                showYearDropdown
                onChange={(date: any) => {
                  dispatch(updateResourceDetails({ resourceId: resourceId, nextFireExtinguishDate: date }));
                  toast.success("Sledeći pp aparat je uspešno izmenjen.");
                }}
                selected={resourceDetails.resource.nextFireExtinguishDate ? new Date(resourceDetails.resource.nextFireExtinguishDate) : null}
                disabled={!(hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive)}
              />
            </span>
            <span className="resource-details-next-fire-in-label greyCell">za</span>
            <span
              className={`resource-details-next-fire-in
              ${resourceDetails.hasAlarmForFireExtinguisher
                  ? "greyBorder red"
                  : "greyBorder"}
            `}
            >
              {resourceDetails.resource.nextFireExtinguishDate &&
                `${DiffDays(resourceDetails.resource.nextFireExtinguishDate)} dana`}
            </span>
            <span className="resource-details-next-6m-at-label greyCell">sledeći 6m tehnički</span>
            <span className="resource-details-next-6m-at greyBorder">
              <DatePicker
                className="rounded-datePicker"
                dateFormat="dd.MM.yy"
                showMonthDropdown
                showYearDropdown
                onChange={(date: any) => {
                  dispatch(updateResourceDetails({ resourceId: resourceId, nextTechnicalInspectionDate: date }));
                  toast.success("Sledeći 6m tehnički je uspešno izmenjen.");
                }}
                selected={resourceDetails.resource.nextTechnicalInspectionDate ? new Date(resourceDetails.resource.nextTechnicalInspectionDate) : null}
                disabled={!(hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive)}
              />
            </span>
            <span className="resource-details-next-6m-in-label greyCell">za</span>
            <span
              className={`resource-details-next-6m-in
              ${resourceDetails.hasAlarmForTechnicalInspection
                  ? "greyBorder red"
                  : "greyBorder"}
            `}
            >
              {resourceDetails.resource.nextTechnicalInspectionDate &&
                `${DiffDays(resourceDetails.resource.nextTechnicalInspectionDate)} dana`}
            </span>

            <span className="resource-details-next-firstaid-at-label greyCell">sledeća prva pomoć</span>
            <span className="resource-details-next-firstaid-at greyBorder">
              <DatePicker
                className="rounded-datePicker"
                dateFormat="dd.MM.yy"
                showMonthDropdown
                showYearDropdown
                onChange={(date: any) => {
                  dispatch(updateResourceDetails({ resourceId: resourceId, nextFirstAidDate: date }));
                  toast.success("Sledeća prva pomoć je uspešno izmenjena.");
                }}
                selected={resourceDetails.resource.nextFirstAidDate ? new Date(resourceDetails.resource.nextFirstAidDate) : null}
                disabled={!(hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive)}
              />
            </span>
            <span className="resource-details-next-firstaid-in-label greyCell">za</span>
            <span
              className={`resource-details-next-firstaid-in
              ${resourceDetails.hasAlarmForFirstAid
                  ? "greyBorder red"
                  : "greyBorder"}
            `}
            >
              {resourceDetails.resource.nextFirstAidDate &&
                `${DiffDays(resourceDetails.resource.nextFirstAidDate)} dana`}
            </span>

            <span className="resource-details-next-insurance-at-label greyCell">sledeći kasko</span>
            <span className="resource-details-next-insurance-at greyBorder">
              <DatePicker
                className="rounded-datePicker"
                dateFormat="dd.MM.yy"
                showMonthDropdown
                showYearDropdown
                onChange={(date: any) => {
                  dispatch(updateResourceDetails({ resourceId: resourceId, nextFullCoverageInsuranceDate: date }));
                  toast.success("Sledeći kasko je uspešno izmenjen.");
                }}
                selected={resourceDetails.resource.nextFullCoverageInsuranceDate ? new Date(resourceDetails.resource.nextFullCoverageInsuranceDate) : null}
                disabled={!(hasFullAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive)}
              />
            </span>
            <span className="resource-details-next-insurance-in-label greyCell">za</span>
            <span
              className={`resource-details-next-insurance-in
              ${resourceDetails.hasAlarmForFullCoverageInsurance
                  ? "greyBorder red"
                  : "greyBorder"}
            `}
            >
              {resourceDetails.resource.nextFullCoverageInsuranceDate &&
                `${DiffDays(resourceDetails.resource.nextFullCoverageInsuranceDate)} dana`}
            </span>

          </>)
            : (
              <>
                <span className="resource-details-charges-summary-label greyCell">zaduženja</span>
                <span className="resource-details-charges-summary greyBorder">
                  {resourceDetails.chargesSummary?.items?.map(chargeSummaryItem => (
                    <div>
                      <span>{chargeSummaryItem.project}</span>
                      <span>({FormatNumberWithUnitOrNull(chargeSummaryItem.quantity, 'kom')})</span>
                    </div>
                  ))}
                </span>
              </>
            )}
          <span className="resource-details-note-label">NAPOMENA</span>
          <div className="resource-details-note">
            <textarea
              spellCheck="false"
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
              onBlur={handleNoteBlur}
              disabled={!(hasWriteAccessTo(user, RESOURCES_MODULE_NAME) && isResourceActive)}
            ></textarea>
          </div>
        </div>
      )
      }
      <UploadPhotoModal
        isOpen={isOpenPhotoModal}
        setIsOpen={setIsOpenPhotoModal}
        resourceId={resourceDetails?.resource.resourceId}
        refresh={refreshResourcePicture}
        aspect={4 / 5}
      />
    </div >
  );
};

export default ResourceDetails;
